import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { providerAPI } from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Clock, LogOut } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const SeekerDashboard = () => {
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [specializationFilter, setSpecializationFilter] = useState('all');
  const [sessionTypeFilter, setSessionTypeFilter] = useState('all');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [providers, searchTerm, cityFilter, specializationFilter, sessionTypeFilter]);

  const fetchProviders = async () => {
    try {
      const response = await providerAPI.getProviders({});
      setProviders(response.data);
      setFilteredProviders(response.data);
    } catch (error) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...providers];

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.profile.bio.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (cityFilter !== 'all') {
      filtered = filtered.filter(p => p.profile.city === cityFilter);
    }

    if (specializationFilter !== 'all') {
      filtered = filtered.filter(p =>
        p.profile.specializations.includes(specializationFilter)
      );
    }

    if (sessionTypeFilter !== 'all') {
      filtered = filtered.filter(p => p.profile.session_type === sessionTypeFilter);
    }

    setFilteredProviders(filtered);
  };

  const cities = [...new Set(providers.map(p => p.profile.city))];
  const allSpecializations = ['Anxiety', 'Depression', 'Trauma', 'Relationship', 'Addiction', 'Career'];

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-[#4B5563]">Loading providers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-border/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="font-heading text-2xl font-bold text-primary">HappyTring</h1>
            <div className="flex items-center gap-4">
              <span className="text-[#4B5563]">Welcome, {user?.name}</span>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="ghost"
                className="text-[#4B5563] hover:text-primary hover:bg-secondary/50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Search and Filters */}
      <div className="bg-gradient-to-b from-secondary to-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2C3333] mb-6">Find Your Provider</h2>

          <div className="space-y-4">
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border-border/60 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary h-12"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select value={cityFilter} onValueChange={setCityFilter}>
                <SelectTrigger data-testid="city-filter" className="rounded-lg bg-white">
                  <SelectValue placeholder="Filter by city" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={specializationFilter} onValueChange={setSpecializationFilter}>
                <SelectTrigger data-testid="specialization-filter" className="rounded-lg bg-white">
                  <SelectValue placeholder="Filter by specialization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Specializations</SelectItem>
                  {allSpecializations.map(spec => (
                    <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sessionTypeFilter} onValueChange={setSessionTypeFilter}>
                <SelectTrigger data-testid="session-type-filter" className="rounded-lg bg-white">
                  <SelectValue placeholder="Filter by session type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Session Types</SelectItem>
                  <SelectItem value="In-person">In-person</SelectItem>
                  <SelectItem value="Online">Online</SelectItem>
                  <SelectItem value="Both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Providers Grid */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProviders.length === 0 ? (
            <div className="text-center py-12" data-testid="no-providers">
              <p className="text-[#4B5563] text-lg">No providers found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredProviders.map((provider, index) => (
                <div
                  key={provider.user.id}
                  data-testid={`provider-card-${index}`}
                  className="bg-white rounded-2xl border border-border/40 p-6 transition-all duration-300 hover:shadow-[0_12px_24px_rgba(0,0,0,0.06)] hover:border-primary/20 group cursor-pointer"
                  onClick={() => navigate(`/provider/${provider.user.id}`)}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-heading font-bold text-primary">
                        {provider.user.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-semibold text-[#2C3333] mb-1 group-hover:text-primary transition-colors">
                        {provider.user.name}
                      </h3>
                      <div className="flex items-center text-sm text-[#4B5563] gap-3">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {provider.profile.experience} years
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {provider.profile.city}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {provider.profile.specializations.slice(0, 3).map((spec) => (
                      <span
                        key={spec}
                        className="bg-secondary text-primary px-3 py-1 rounded-full text-xs font-medium tracking-wide uppercase"
                      >
                        {spec}
                      </span>
                    ))}
                    {provider.profile.specializations.length > 3 && (
                      <span className="bg-secondary text-primary px-3 py-1 rounded-full text-xs font-medium">
                        +{provider.profile.specializations.length - 3} more
                      </span>
                    )}
                  </div>

                  <p className="text-[#4B5563] text-sm mb-4 leading-relaxed line-clamp-3">
                    {provider.profile.bio}
                  </p>

                  <Button
                    data-testid={`view-profile-btn-${index}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/provider/${provider.user.id}`);
                    }}
                    className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-2 font-medium transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    View Profile
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeekerDashboard;